"use client"

import React, { useState } from "react"
import { useSearchParams } from "next/navigation"
import { Slot } from "@radix-ui/react-slot"

import { GetSlug } from "@/lib/utils"
import {
  CardAnnuaire,
  ChipFilterList,
  ICardAnnuaireProps,
  Pagination,
  TPagination,
} from "@/components/ui/molecules"

export interface AnnuaireProps {
  asChild?: boolean
  className?: string
  componentTitle?: string
  cardAnnuaires: ICardAnnuaireProps[]
  pageUrl?: string
  preview?: boolean
  codename: string
  totalPageCount?: number
}

const Annuaire = React.forwardRef<HTMLDivElement, AnnuaireProps>(
  (
    {
      asChild = false,
      componentTitle,
      className,
      cardAnnuaires,
      totalPageCount,
      preview = false,
      codename,
      pageUrl,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"
    const [selectedTagLabel, setSelectedTagLabel] = useState<string | null>(
      null
    )

    const searchParams = useSearchParams()
    const currentPageParam = searchParams.get("page")
    const currentPage = currentPageParam ? parseInt(currentPageParam, 10) : 1

    // In order make pagination work with preview mode, we need to change the pageUrl to the codename
    if (preview) {
      pageUrl = codename
    }

    const limitNews = 10

    // Apply filter first (this affects total count and pagination)
    const filteredAnnuaires = (cardAnnuaires ?? []).filter((card) => {
      if (!selectedTagLabel) return true
      return `tag-${GetSlug(card.tagLabel)}` === selectedTagLabel
    })

    // Pagination calculations based on filtered list
    const totalAnnuairesCount = filteredAnnuaires.length
    const pageCount = Math.max(1, Math.ceil(totalAnnuairesCount / limitNews))
    const currentPageClamped = Math.min(
      Math.max(currentPage || 1, 1),
      pageCount
    )
    const offset = (currentPageClamped - 1) * limitNews
    const annuairesToShow = filteredAnnuaires.slice(offset, offset + limitNews)

    const showPagination = totalAnnuairesCount > limitNews

    // Build pagination list
    const paginationList: TPagination[] = []
    for (let i = 1; i <= pageCount; i++) {
      const url = i === 1 ? `${pageUrl}` : `${pageUrl}?page=${i}`
      paginationList.push({
        label: i,
        isCurrent: i === currentPage,
        url,
      })
    }

    return (
      <Component
        className="mb-17 mt-10 bg-neutral-200 lg:mt-15"
        ref={ref}
        {...props}
      >
        <div className="container">
          <ChipFilterList
            componentTitle={componentTitle}
            list={cardAnnuaires}
            className="mb-9 lg:mb-12"
            selectedTagLabel={selectedTagLabel}
            setSelectedTagLabel={setSelectedTagLabel}
          />
          {annuairesToShow.map((annuaire) => {
            return (
              <CardAnnuaire className="mb-8" key={annuaire.id} {...annuaire} />
            )
          })}
        </div>
        {showPagination ? (
          <footer className="flex items-center justify-center pt-12">
            <Pagination list={paginationList} />
          </footer>
        ) : null}
      </Component>
    )
  }
)
Annuaire.displayName = "Annuaire"

export { Annuaire }
